export interface JwtPayload {
  exp?: number;
  [key: string]: unknown;
}

/**
 * Defensive base64url JWT payload decode. Never throws — a malformed or
 * corrupt token is exactly the case this exists to catch, not propagate.
 */
export function decodeJwtPayload(token: string | null | undefined): JwtPayload | null {
  if (!token || typeof token !== 'string') {
    return null;
  }

  const parts = token.split('.');
  if (parts.length !== 3) {
    return null;
  }

  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const json = decodeURIComponent(
      atob(padded)
        .split('')
        .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
        .join('')
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

/**
 * Token existence alone is never proof of authentication — it must also
 * decode, carry an `exp` claim, and that claim must still be in the future.
 */
export function isTokenExpired(token: string | null | undefined): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.exp !== 'number') {
    return true;
  }
  return Date.now() >= payload.exp * 1000;
}

/**
 * Milliseconds remaining until the token's `exp` claim, or null if the
 * token is missing, malformed, has no `exp`, or has already expired —
 * callers should treat null as "nothing to schedule".
 */
export function getMsUntilExpiry(token: string | null | undefined): number | null {
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.exp !== 'number') {
    return null;
  }
  const msRemaining = payload.exp * 1000 - Date.now();
  return msRemaining > 0 ? msRemaining : null;
}
